// Full lab report view, opened from the "Generate" button in the per-order
// Report column (or the patient-level consolidated report button) inside
// IpdLabOrders.js. Pulls parameter/result rows from whatever has been typed
// into the Initial Observation panel for each order, falling back to the
// order's own baked-in `results` (used for the seeded demo orders that
// haven't had anything entered yet) so the report always has something to
// show.
import React, { useContext, useMemo, useRef, useState, useEffect } from "react";
import axios from "axios";
import { submitDiagnosticReport } from "../utils/submitDiagnosticReport";
import { toast } from "react-toastify";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../login/PermissionContext";
import {
  printLabReport,
  downloadLabReport,
  resolveReferringDoctor,
  withDoctorPrefix,
} from "./utils/printLabReport";
import InitialObservationPanel from "./InitialObservationPanel";
import SvgIcon from "../ipd-billing/SvgIcon";
import { C, getAuthHeaders } from "../ipd-billing/constants";
import { cardStyle } from "../rule-builder/ui";
import {
  READY_STATUSES,
  REPORT_PUBLISHED_STATUSES,
  formatDateTime,
  computeAge,
  flattenObservationPanels,
  observationCommentText,
  readLabReport,
  fetchLabReportFromServer,
  readLabReportPanels,
  panelsFromReportRows,
  resolveOrderPatientLabTestId,
  isDiagnosticReportRejected,
  isDiagnosticReportPublished,
} from "./labOrdersShared";
import DiagnosticSignatories, {
  useDiagnosticReview,
} from "../diagnostic-reports/DiagnosticSignatories";
import { isReviewSubmitted } from "../diagnostic-reports/diagnosticReportApi";

const getOrderResults = (order, observations, serverReports = {}) => {
  const livePanels = observations[order.orderNo];
  const liveRows = flattenObservationPanels(livePanels);
  const liveComment = observationCommentText(livePanels);
  if (liveRows.length > 0) {
    return { rows: liveRows, interpretation: liveComment || null };
  }
  // The report the lab user finalized, as saved on the server.
  const saved = serverReports[order.orderNo];
  if (saved?.rows?.length > 0) {
    return {
      rows: saved.rows,
      interpretation:
        observationCommentText(saved.panels) || saved.interpretation || null,
    };
  }
  // Nothing typed in this session — fall back to what the lab user stored when
  // they generated the report, so a later View (theirs, or the phlebotomist's)
  // still shows the results rather than an empty table.
  const stored = readLabReport(order.orderNo);
  if (stored) {
    return {
      rows: stored.rows,
      interpretation: observationCommentText(stored.panels) || liveComment || null,
    };
  }
  // A comment can be typed before any value is — readLabReport ignores a panel
  // with no results, so return it here rather than dropping it.
  if (liveComment) return { rows: [], interpretation: liveComment };
  if (order.results && order.results.length > 0) {
    return {
      rows: order.results,
      interpretation: order.interpretation || null,
    };
  }
  return { rows: [], interpretation: null };
};

const STATUS_ACCENT = {
  Completed: { border: "#15803D", bg: "#ECFDF5", iconBg: "#D1FAE5" },
  Verified: { border: "#0F766E", bg: "#F0FDFA", iconBg: "#CCFBF1" },
  Processing: { border: "#C2410C", bg: "#FFF7ED", iconBg: "#FFEDD5" },
};
const DEFAULT_ACCENT = { border: "#9CA3AF", bg: "#F9FAFB", iconBg: "#F3F4F6" };

// Whether the PDF carries the hospital's header/footer. Off is for pre-printed
// stationery, where the paper already has them.
function LetterheadToggle({ checked, onChange }) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        marginRight: "auto",
        fontSize: 12,
        color: C.textSub,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ cursor: "pointer" }}
      />
      With letterhead
    </label>
  );
}

function InfoTile({ label, value }) {
  return (
    <div style={{ ...cardStyle, padding: "10px 14px", flex: 1, minWidth: 140 }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: C.textSub,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </div>
      <div
        style={{ fontSize: 14, fontWeight: 700, color: C.text, marginTop: 2 }}
      >
        {value}
      </div>
    </div>
  );
}

// Compact button for the per-order observation toolbar (Edit / Amend / Confirm).
function ObservationButton({ tone = "plain", disabled, onClick, icon, children }) {
  const primary = tone === "primary";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "5px 11px",
        borderRadius: 6,
        border: primary ? "none" : `1.5px solid ${C.border}`,
        background: primary ? C.primary : "#fff",
        color: primary ? "#fff" : C.text,
        fontSize: 11,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
      }}
    >
      {icon && (
        <SvgIcon name={icon} size={13} color={primary ? "#fff" : C.textSub} />
      )}
      {children}
    </button>
  );
}

const ABDM_FEATURES = [
  { icon: "shield", label: "Secure & Verified" },
  { icon: "check", label: "ABDM Compliant" },
  { icon: "users", label: "Patient Controlled" },
  { icon: "sync", label: "Instant Access" },
];

// The "Report by" name box is hidden for now; flip to true to bring it back.
const SHOW_REPORT_BY = false;

export default function LabReportModal({
  patient,
  observations,
  onObservationChange,
  focusOrderNo,
  // Anyone who isn't the lab user (phlebotomy today) opens this to read a
  // published report, never to enter results — the observation editor is
  // hidden for them.
  readOnly = false,
  // Sign-off for one order's results. Resolves falsy when the status POST was
  // rejected, so the card can stay un-confirmed. Optional — without it the
  // Confirm button isn't offered.
  onConfirmResults,
  // After finalize, lab user picks one or more pathologists for review.
  onSubmitForReview,
  canSubmitForReview = false,
  footerExtra = null,
  onClose,
}) {
  const generatedAt = useMemo(() => new Date(), []);
  const orders = patient.orders;
  const testsCount = orders.length;
  const primaryOrder =
    orders.find((o) => o.orderNo === focusOrderNo) || orders[0];
  // DOB is the better source when the admission carries one; otherwise fall
  // back to the age the orders endpoint reports for the patient.
  const age = computeAge(patient.dob) ?? patient.age ?? null;
  const referringDoctor = resolveReferringDoctor(primaryOrder, patient);
  const orderLabTestIds = useMemo(
    () => orders.map((o) => resolveOrderPatientLabTestId(o)).filter(Boolean),
    [orders]
  );
  const signatoriesReview = useDiagnosticReview({
    extraIds: orderLabTestIds,
    roleType: "pathologist",
  });
  const signatories = signatoriesReview.signatories;
  const rejectionReason =
    signatoriesReview.rejection.reason ||
    orders.find((o) => o.rejectionReason)?.rejectionReason ||
    "";

  const isOrderPublished = (order) => {
    if (isDiagnosticReportPublished(order)) return true;
    const pltId = resolveOrderPatientLabTestId(order);
    return pltId && signatoriesReview.publishedByPltId[pltId];
  };

  // Sent for review and still with the diagnostic doctor: the session knows
  // it from the Submit click, the assignments know it after a reload. A
  // rejection reopens the report.
  const isOrderSubmitted = (order) => {
    if (
      isOrderPublished(order) ||
      isDiagnosticReportRejected(order) ||
      signatoriesReview.rejection.rejected
    ) {
      return false;
    }
    if (isReviewSubmitted(order)) return true;
    const pltId = resolveOrderPatientLabTestId(order);
    return Boolean(pltId && signatoriesReview.submittedByPltId[pltId]);
  };

  // Lock the page behind the modal so scrolling/wheeling over the report
  // doesn't also scroll the lab orders table underneath it.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Saved reports of the finalized orders, fetched when the report opens.
  const [serverReports, setServerReports] = useState({});
  const reportedOrderNosKey = orders
    .filter((o) => REPORT_PUBLISHED_STATUSES.includes(o.status))
    .map((o) => o.orderNo)
    .join("|");
  useEffect(() => {
    if (!reportedOrderNosKey) return undefined;
    let cancelled = false;
    Promise.all(
      reportedOrderNosKey.split("|").map(async (no) => [
        no,
        await fetchLabReportFromServer(no),
      ]),
    ).then((entries) => {
      if (cancelled) return;
      setServerReports(
        Object.fromEntries(entries.filter(([, report]) => report)),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [reportedOrderNosKey]);

  // Which order's observation editor is open. Once results exist the editor
  // collapses behind an Edit/Amend button — a saved result shouldn't sit in a
  // live input where a stray keystroke silently rewrites it.
  const [editingOrderNo, setEditingOrderNo] = useState(null);
  // `patient.orders` is a snapshot taken when the modal opened, so a status
  // moved to Completed by Confirm below won't show up in it. Track the
  // confirmations made here too, seeded from whatever was already signed off.
  const [confirmedOrderNos, setConfirmedOrderNos] = useState(
    () =>
      new Set(
        orders
          .filter(
            (o) =>
              REPORT_PUBLISHED_STATUSES.includes(o.status) &&
              !isDiagnosticReportRejected(o),
          )
          .map((o) => o.orderNo),
      ),
  );
  const [confirmingOrderNo, setConfirmingOrderNo] = useState(null);
  const showRejection =
    (signatoriesReview.rejection.rejected ||
      orders.some((o) => isDiagnosticReportRejected(o))) &&
    !orders.every((o) => confirmedOrderNos.has(o.orderNo));

  // Reopening the editor on results that came back from the store: the panels
  // are normally still there, but a report stored by an older build kept only
  // the flat rows — rebuild a panel from those so Amend is never a dead end.
  const handleEditObservation = (order, rows) => {
    const live = observations[order.orderNo];
    if (!Array.isArray(live) || live.length === 0) {
      const stored = readLabReportPanels(order.orderNo);
      const seeded =
        stored.length > 0 ? stored : panelsFromReportRows(rows, order.service);
      if (seeded.length > 0) onObservationChange(order.orderNo, seeded);
    }
    setEditingOrderNo(order.orderNo);
  };

  const handleConfirmOrder = async (order) => {
    if (!onConfirmResults) return;
    setConfirmingOrderNo(order.orderNo);
    try {
      const ok = await onConfirmResults(order);
      if (ok === false) return;
      setConfirmedOrderNos((prev) => new Set(prev).add(order.orderNo));
      setEditingOrderNo((prev) => (prev === order.orderNo ? null : prev));
    } finally {
      setConfirmingOrderNo(null);
    }
  };

  // ABDM linking is a user-initiated action inside the report itself — it
  // always starts unlinked here regardless of any pre-set patient.abdm flag,
  // and flips to "Linked" only once the 4-step handshake below completes.
  const [abdmLinked, setAbdmLinked] = useState(false);
  const [abdmTransactionId, setAbdmTransactionId] = useState(null);
  const [abdmLinking, setAbdmLinking] = useState(false);

  // Supplies the facility's uploaded logo to the PDF letterhead; falls back to
  // the bundled one when a facility has no branding configured.
  const brandingData = useContext(BrandingContext);
  // Facility address for the letterhead, from get_logged_in_user_info/ — the
  // same source the billing invoice uses. Reading it here rather than from
  // sessionStorage matters: only the landing screens write those keys, so a
  // technician who logs straight into this workspace has none of them.
  const { userInfo } = usePermissions();
  // On by default — a report handed to a patient normally needs the hospital's
  // header. Hospitals running pre-printed stationery through the tray turn it
  // off; the page margins stay the same either way so the content still lands
  // inside their printed header and footer.
  const [withLetterhead, setWithLetterhead] = useState(true);
  const [printing, setPrinting] = useState(false);

  // Who prepared the report — printed over the "Prepared By" rule in the
  // letterhead footer. Typed here rather than guessed: the logged-in account is
  // only a default, since a workspace login is often shared across a shift.
  const [technicianName, setTechnicianName] = useState(
    () =>
      sessionStorage.getItem("labTechnicianName") ||
      primaryOrder?.technician ||
      sessionStorage.getItem("name") ||
      "",
  );
  // Remembered for the session so the next report in the same shift opens
  // pre-filled instead of asking again.
  useEffect(() => {
    sessionStorage.setItem("labTechnicianName", technicianName);
  }, [technicianName]);

  const technicianDisplay = withDoctorPrefix(technicianName);
  // Whether the current click's press landed on the backdrop — see its comment.
  const backdropPressRef = useRef(false);

  // The PDF is built from the data, not captured from this modal — see
  // utils/printLabReport.js. Results are resolved here because getOrderResults
  // is where the observation/stored/baked-in fallback chain lives.
  const buildReportPayload = () => ({
    patient: { ...patient, age },
    generatedAt: generatedAt.toISOString(),
    technician: technicianDisplay,
    brandingData,
    userInfo,
    withLetterhead,
    orders: orders.map((o) => {
      const { rows, interpretation } = getOrderResults(o, observations, serverReports);
      return {
        orderNo: o.orderNo,
        service: o.service,
        cpt: o.cpt,
        ordered: o.ordered,
        doctor: o.doctor,
        department: o.department,
        technician: o.technician,
        status: o.status,
        pending: !REPORT_PUBLISHED_STATUSES.includes(o.status),
        rows,
        interpretation,
        patientLabTestId: resolveOrderPatientLabTestId(o),
      };
    }),
    signatories,
  });

  const handlePrint = async () => {
    setPrinting(true);
    try {
      await printLabReport(buildReportPayload());
    } finally {
      setPrinting(false);
    }
  };

  // Download saves the file. It used to call handlePrint, which opens the PDF
  // in a tab and fires the viewer's print dialog — the same thing the Print
  // button does, which is why the two buttons behaved identically.
  const handleDownload = async () => {
    setPrinting(true);
    try {
      await downloadLabReport(buildReportPayload());
    } finally {
      setPrinting(false);
    }
  };

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const canLinkAbha =
    Boolean(patient.abhaId) && Boolean(primaryOrder.careContextReferenceId);

  // Same 4-call ABDM handshake as UploadReprortTable.js's linkToAbdm: store
  // the pending care-context link, run the demographic auth init/confirm
  // round-trip against the patient's ABHA address, then attach the resulting
  // access token + care context to the patient's ABHA record.
  const handleLinkAbha = async () => {
    if (!patient.abhaId) {
      toast.error("Patient has no ABHA address on file");
      return;
    }
    if (!primaryOrder.careContextReferenceId) {
      toast.error("No care context reference available for this report yet");
      return;
    }

    setAbdmLinking(true);
    try {
      const headers = getAuthHeaders();
      const dob = new Date(patient.dob);
      if (Number.isNaN(dob.getTime())) {
        toast.error("Patient date of birth is missing or invalid");
        return;
      }
      const pad = (n) => String(n).padStart(2, "0");
      // ABDM expects YYYY-DD-MM here (matches the working demo integration's
      // formatDateOfBirth), not the usual YYYY-MM-DD.
      const dateOfBirth = `${dob.getFullYear()}-${pad(dob.getDate())}-${pad(dob.getMonth() + 1)}`;

      // Submit the report first (as DiagnosticReportsUnderwriter does); a
      // failure lands in the catch below and stops the linking.
      const { rows, interpretation } = getOrderResults(primaryOrder, observations, serverReports);
      await submitDiagnosticReport({
        backendUrl: REACT_APP_BACKEND_ROUTE_URL,
        headers,
        patientId: patient.patientId,
        careContextId: primaryOrder.careContextReferenceId,
        jobId: primaryOrder.jobId ?? null,
        panels: [
          {
            name: primaryOrder.service,
            conclusion: interpretation || "",
            observations: rows.map((r) => ({
              description: r.param,
              value: r.value,
              unit: r.unit,
            })),
          },
        ],
      });

      const pendingForm = new FormData();
      pendingForm.append("patient_id", patient.patientId);
      pendingForm.append(
        "careContextReferenceID",
        primaryOrder.careContextReferenceId,
      );
      const pendingRes = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}save_pending_care_context_linking/`,
        pendingForm,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } },
      );

      const initRes = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}auth_init_workflow/`,
        JSON.stringify({
          query: {
            id: patient.abhaId,
            purpose: "KYC_AND_LINK",
            requester: {
              type: "HIP",
              id: sessionStorage.getItem("facility_name"),
            },
            authMode: "DEMOGRAPHICS",
          },
        }),
        { headers },
      );
      if (initRes.data?.status !== "Success") {
        toast.error(
          initRes.data?.message || "Could not initiate ABHA verification",
        );
        return;
      }

      const confirmRes = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}auth_confirm_workflow/`,
        JSON.stringify({
          transactionId: initRes.data.transaction_id,
          credential: {
            demographic: {
              name: patient.name,
              gender: patient.gender,
              dateOfBirth,
              authCode: "",
            },
          },
        }),
        { headers },
      );
      if (confirmRes.data?.status !== "Success") {
        toast.error(confirmRes.data?.message || "ABHA verification failed");
        return;
      }

      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}add_context_workflow/`,
        JSON.stringify({
          link: {
            accessToken: confirmRes.data.access_token,
            patient: {
              referenceNumber: patient.patientId,
              display: patient.abhaId,
              careContexts: [
                {
                  display:
                    primaryOrder.careContextDisplay || primaryOrder.service,
                  referenceNumber: primaryOrder.careContextReferenceId,
                },
              ],
            },
          },
          pending_care_context_transaction_id:
            pendingRes.data?.pending_care_context_transaction_id,
        }),
        { headers },
      );

      if (res.data?.status === "Success") {
        setAbdmTransactionId(
          pendingRes.data?.pending_care_context_transaction_id,
        );
        setAbdmLinked(true);
        toast.success(`${patient.name}'s report linked to ABHA`);
      } else {
        toast.error(res.data?.message || "some error occurred");
      }
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.message || err?.message || "ABHA linking failed");
    } finally {
      setAbdmLinking(false);
    }
  };

  return (
    <>
      {/* Backdrop. It closes on a click that both STARTED and ended on it —
          a bare onClick also fires for a stray mouseup that landed here after
          the press happened somewhere else (dismissing a browser dialog, or
          releasing a drag that began inside the report), which read as the
          modal closing itself. */}
      <div
        onMouseDown={(e) => {
          backdropPressRef.current = e.target === e.currentTarget;
        }}
        onClick={(e) => {
          if (backdropPressRef.current && e.target === e.currentTarget) onClose();
          backdropPressRef.current = false;
        }}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.55)",
          zIndex: 2000,
        }}
      />
      <div
        className="lab-report-modal"
        style={{
          position: "fixed",
          inset: "3% 8%",
          zIndex: 2001,
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <style>{`
          @media print {
            body * { visibility: hidden; }
            .lab-report-modal, .lab-report-modal * { visibility: visible; }
            .lab-report-modal { position: absolute; inset: 0 !important; box-shadow: none !important; border-radius: 0 !important; }
            .lab-report-no-print { display: none !important; }
          }
        `}</style>

        <div
          className="lab-report-no-print"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 8,
            padding: "10px 16px",
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
          }}
        >
          <LetterheadToggle checked={withLetterhead} onChange={setWithLetterhead} />
          <button
            type="button"
            onClick={handleDownload}
            disabled={printing}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 7,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              color: C.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: printing ? "default" : "pointer",
              opacity: printing ? 0.6 : 1,
              fontFamily: "inherit",
            }}
          >
            <SvgIcon name="download" size={14} color={C.textSub} />
            {printing ? "Preparing…" : "Download"}
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={printing}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 7,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              color: C.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: printing ? "default" : "pointer",
              opacity: printing ? 0.6 : 1,
              fontFamily: "inherit",
            }}
          >
            <SvgIcon name="printer" size={14} color={C.textSub} />
            {printing ? "Preparing…" : "Print"}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 7,
              border: "none",
              background: C.primary,
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <SvgIcon name="x" size={14} color="#fff" />
            Close
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "24px 32px" }}>
          {/* ── Patient header ─────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", gap: 12 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: C.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <SvgIcon name="users" size={20} color={C.primary} />
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: C.text }}>
                  {patient.name}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 18,
                    flexWrap: "wrap",
                    marginTop: 4,
                    fontSize: 12,
                    color: C.textSub,
                  }}
                >
                  <span>
                    UHID <b style={{ color: C.primary }}>{patient.uhid}</b>
                  </span>
                  <span>
                    Age{" "}
                    <b style={{ color: C.text }}>
                      {age !== null ? `${age}Y` : "-"}
                    </b>
                  </span>
                  {/* Same fallback the PDF prints (resolveReferringDoctor):
                      the order names the doctor when it can, otherwise the
                      admission does. */}
                  <span>
                    Ref. By Doctor{" "}
                    <b style={{ color: C.text }}>{referringDoctor || "-"}</b>
                  </span>
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: 12, color: C.textSub }}>
              <div>
                Report Generated{" "}
                <b style={{ color: C.text }}>
                  {formatDateTime(generatedAt.toISOString())}
                </b>
              </div>
              <div style={{ marginTop: 2 }}>
                Total Tests <b style={{ color: C.text }}>{testsCount}</b>
              </div>
            </div>
          </div>

          {/* ── Order summary tiles ────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 18,
            }}
          >
            <InfoTile label="Order ID" value={primaryOrder.orderNo} />
            <InfoTile label="Total Orders" value={testsCount} />
            <InfoTile label="Total Tests" value={testsCount} />
            <InfoTile
              label="Order Date & Time"
              value={formatDateTime(primaryOrder.ordered)}
            />
          </div>

          {showRejection ? (
            <div
              className="lab-report-no-print"
              style={{
                marginTop: 14,
                padding: "12px 14px",
                borderRadius: 8,
                border: "1px solid #f5b7b1",
                background: "#fdedec",
                color: "#922b21",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700 }}>
                Reviewer rejected this report
              </div>
              {rejectionReason ? (
                <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.45 }}>
                  {rejectionReason}
                </div>
              ) : null}
              <div style={{ fontSize: 11, marginTop: 6 }}>
                Amend the results, finalize again, then submit for review.
              </div>
            </div>
          ) : null}

          {/* Report-by field. Sits at the right edge and stays narrow — it is a
              name, not a paragraph. Editable here, printed on the PDF, but kept
              off the printed sheet's own markup (lab-report-no-print) because
              the PDF is built from data, not captured from this DOM.
              "Dr." is fixed, not chosen: the lab report is signed by a doctor. */}
          {SHOW_REPORT_BY && (
          <div
            className="lab-report-no-print"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 8,
              marginTop: 10,
            }}
          >
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: C.textSub,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                whiteSpace: "nowrap",
              }}
            >
              Report by
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: `1.5px solid ${C.border}`,
                borderRadius: 6,
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  padding: "6px 0 6px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.text,
                }}
              >
                Dr.
              </span>
              <input
                value={technicianName.replace(/^dr\.?\s*/i, "")}
                onChange={(e) => setTechnicianName(e.target.value)}
                placeholder="Technician name"
                style={{
                  width: 180,
                  padding: "6px 10px",
                  border: "none",
                  outline: "none",
                  fontSize: 12,
                  fontFamily: "inherit",
                  color: C.text,
                }}
              />
            </div>
          </div>
          )}

          {/* ── Detailed results ───────────────────────────────────── */}
          {/* No separate "Investigations Summary" table — every test in this
              sample is listed below with its own status and results, so the
              summary only repeated it. */}
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: C.primary,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              marginTop: 24,
              marginBottom: 8,
            }}
          >
            Detailed Results
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {orders.map((o, idx) => {
              const ready = REPORT_PUBLISHED_STATUSES.includes(o.status);
              const accent = STATUS_ACCENT[o.status] || DEFAULT_ACCENT;
              const { rows, interpretation } = getOrderResults(o, observations, serverReports);
              return (
                <div
                  key={o.orderNo}
                  style={{
                    ...cardStyle,
                    borderLeft: `4px solid ${accent.border}`,
                    padding: 16,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          background: accent.iconBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      >
                        <SvgIcon name="flask" size={14} color={accent.border} />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: C.text,
                          }}
                        >
                          {idx + 1}. {o.service}
                        </div>
                        {/* Nothing under the test name. Order number, order
                            date, referring doctor and technician are stated
                            once in the header above, and the CPT is a billing
                            code — it has no business on the document handed to
                            a patient. */}
                      </div>
                    </div>
                    {/* No status here. This is the report, not the worklist —
                        the workflow stage ("Sample Sent", "Processing") belongs
                        on the orders dashboard, not on the document a patient
                        is handed. */}
                  </div>

                  {!readOnly &&
                    (() => {
                      const hasResults = rows.length > 0;
                      const editing = editingOrderNo === o.orderNo;
                      const confirmed = confirmedOrderNos.has(o.orderNo);
                      const confirming = confirmingOrderNo === o.orderNo;
                      const published = isOrderPublished(o);
                      const needsRevision =
                        isDiagnosticReportRejected(o) && !confirmed;
                      const lockedAfterPublish =
                        (published || isOrderSubmitted(o)) && !needsRevision;
                      // Nothing recorded yet → the search box IS the task, so
                      // it stays open. Once there are results it hides until
                      // the technician explicitly asks to change them.
                      const editorOpen = !hasResults || editing;
                      return (
                        <div
                          className="lab-report-no-print"
                          style={{ marginTop: 12 }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              flexWrap: "wrap",
                              gap: 8,
                              marginBottom: 6,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: C.textSub,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              Observation
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              {confirmed && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    background: "#ECFDF5",
                                    color: "#15803D",
                                    fontSize: 11,
                                    fontWeight: 700,
                                  }}
                                >
                                  <SvgIcon
                                    name="check"
                                    size={12}
                                    color="#15803D"
                                  />
                                  Report Finalized
                                </span>
                              )}
                              {hasResults && !editing && !lockedAfterPublish && (
                                <ObservationButton
                                  icon="edit"
                                  onClick={() => handleEditObservation(o, rows)}
                                >
                                  {/* After sign-off a change is an amendment,
                                      not an edit — say so. */}
                                  {confirmed ? "Amend Results" : "Edit Results"}
                                </ObservationButton>
                              )}
                              {hasResults && editing && (
                                <ObservationButton
                                  icon="check"
                                  onClick={() => setEditingOrderNo(null)}
                                >
                                  Done Editing
                                </ObservationButton>
                              )}
                              {hasResults && !confirmed && onConfirmResults && (
                                <ObservationButton
                                  tone="primary"
                                  icon="check"
                                  disabled={confirming}
                                  onClick={() => handleConfirmOrder(o)}
                                >
                                  {confirming
                                    ? "Finalizing…"
                                    : needsRevision
                                    ? "Finalize Revised Report"
                                    : "Finalize Report"}
                                </ObservationButton>
                              )}
                            </div>
                          </div>
                          {editorOpen ? (
                            <InitialObservationPanel
                              data={observations[o.orderNo] || []}
                              setData={(next) => {
                                onObservationChange(o.orderNo, next);
                                // First value typed while entering initial results
                                // flips hasResults true — pin editing mode so the
                                // panel (and its overlay) don't collapse mid-entry.
                                setEditingOrderNo((prev) =>
                                  prev === o.orderNo ? prev : o.orderNo,
                                );
                              }}
                            />
                          ) : (
                            <div style={{ fontSize: 12, color: C.textSub }}>
                              {rows.length} parameter
                              {rows.length === 1 ? "" : "s"} recorded
                              {confirmed
                                ? " and confirmed."
                                : " — confirm to release this report."}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                  {rows.length > 0 && (
                    <div
                      style={{
                        marginTop: 12,
                        overflow: "hidden",
                        borderRadius: 8,
                        border: `1px solid ${C.border}`,
                      }}
                    >
                      <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr
                            style={{
                              background: "#f8f9fa",
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.textSub,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            <th
                              style={{ textAlign: "left", padding: "7px 10px" }}
                            >
                              Parameter
                            </th>
                            <th
                              style={{ textAlign: "left", padding: "7px 10px" }}
                            >
                              Result
                            </th>
                            <th
                              style={{ textAlign: "left", padding: "7px 10px" }}
                            >
                              Unit
                            </th>
                            <th
                              style={{ textAlign: "left", padding: "7px 10px" }}
                            >
                              Reference Range
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => (
                            <tr
                              key={i}
                              style={{ borderTop: `1px solid ${C.border}` }}
                            >
                              <td
                                style={{
                                  padding: "7px 10px",
                                  fontSize: 12,
                                  color: C.text,
                                }}
                              >
                                {r.param}
                              </td>
                              <td
                                style={{
                                  padding: "7px 10px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: C.text,
                                }}
                              >
                                {r.value}
                              </td>
                              <td
                                style={{
                                  padding: "7px 10px",
                                  fontSize: 12,
                                  color: C.textSub,
                                }}
                              >
                                {r.unit || "-"}
                              </td>
                              <td
                                style={{
                                  padding: "7px 10px",
                                  fontSize: 12,
                                  color: C.textSub,
                                }}
                              >
                                {r.refRange || "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {interpretation && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "8px 12px",
                        borderRadius: 7,
                        background: "#F9FAFB",
                        border: `1px solid ${C.border}`,
                        color: C.text,
                        fontSize: 12,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: C.textSub,
                          marginBottom: 3,
                        }}
                      >
                        Interpretation
                      </div>
                      {/* Free text typed in the panel comment box — keep the
                          technician's own line breaks. */}
                      <div style={{ whiteSpace: "pre-wrap" }}>{interpretation}</div>
                    </div>
                  )}

                  {/* No "Report Pending" banner here: the StatusPill in this
                      card's top-right already states the stage ("Sample Sent",
                      "Processing", …). The banner repeated it in alarm colours
                      on every test that simply hadn't been run yet. */}

                  {ready && rows.length === 0 && (
                    <div
                      style={{ marginTop: 10, fontSize: 12, color: C.textSub }}
                    >
                      No parameters recorded for this test.
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── ABHA / ABDM linking ─────────────────────────────────── */}
          <div style={{ ...cardStyle, padding: 16, marginTop: 24 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", gap: 10, maxWidth: 560 }}>
                <SvgIcon name="shield" size={18} color={C.primary} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                    ABHA (ABDM) Linking
                  </div>
                  <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>
                    Link this report to the patient's ABHA (ABDM) account to
                    securely share and access health records.
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 16,
                      flexWrap: "wrap",
                      marginTop: 8,
                    }}
                  >
                    {ABDM_FEATURES.map((f) => (
                      <div
                        key={f.label}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 11,
                          color: C.textSub,
                        }}
                      >
                        <SvgIcon name={f.icon} size={13} color={C.success} />
                        {f.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {abdmLinked ? (
                <div style={{ textAlign: "right" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "4px 12px",
                      borderRadius: 20,
                      background: "#ECFDF5",
                      color: "#15803D",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    Linked
                  </span>
                  <div style={{ fontSize: 11, color: C.textSub, marginTop: 4 }}>
                    {abdmTransactionId}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="lab-report-no-print"
                  onClick={handleLinkAbha}
                  // Pushing a report into the patient's ABDM record is the
                  // reporting lab's call, not a read-only viewer's.
                  disabled={abdmLinking || !canLinkAbha || readOnly}
                  title={
                    readOnly
                      ? "Only the reporting lab can link this report to ABHA"
                      : canLinkAbha
                        ? undefined
                        : "ABHA address not available for this report yet"
                  }
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "9px 18px",
                    borderRadius: 8,
                    border: "none",
                    background: C.primary,
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor:
                      abdmLinking || !canLinkAbha || readOnly
                        ? "not-allowed"
                        : "pointer",
                    opacity: abdmLinking || !canLinkAbha || readOnly ? 0.6 : 1,
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  {abdmLinking ? "Linking…" : "Link with ABHA"}
                </button>
              )}
            </div>
          </div>

          {/* ── Footer: download ───────────────────── */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 16,
              marginTop: 24,
              paddingTop: 16,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            {footerExtra ? (
              <div
                className="lab-report-no-print"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                  marginRight: "auto",
                }}
              >
                {footerExtra}
              </div>
            ) : null}
            {canSubmitForReview &&
              onSubmitForReview &&
              confirmedOrderNos.has(primaryOrder.orderNo) &&
              !isOrderPublished(primaryOrder) && (
                <button
                  type="button"
                  className="lab-report-no-print"
                  onClick={() => onSubmitForReview(primaryOrder, orders)}
                  disabled={isOrderSubmitted(primaryOrder)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "9px 18px",
                    borderRadius: 8,
                    border: "none",
                    background: C.primary,
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: isOrderSubmitted(primaryOrder)
                      ? "not-allowed"
                      : "pointer",
                    opacity: isOrderSubmitted(primaryOrder) ? 0.6 : 1,
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                    height: "fit-content",
                  }}
                >
                  <SvgIcon name="users" size={15} color="#fff" />
                  {isOrderSubmitted(primaryOrder)
                    ? "Submitted for Review"
                    : "Submit for Review"}
                </button>
              )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 10,
              fontSize: 10,
              color: C.textSub,
            }}
          >
            <span>
              {signatories.length > 0
                ? "Digitally signed by the reviewing consultant."
                : "This is a system generated report and does not require physical signature."}
            </span>
            <span>Page 1 of 1</span>
          </div>
          <DiagnosticSignatories signatories={signatories} />
        </div>
      </div>
    </>
  );
}
